import type { Moment } from "moment";
import type {
    MarketingActionSlot,
    TriggerRefValueType,
} from "src/types/action/MarketingActionSlot";
import type { MarketingActionSlotMetaData } from "src/types/action/MarketingActionSlotMetaData";
import type { DayOfTheWeek } from "src/types/enum/DayOfTheWeek";
import type { ItemNameCategoryDiscount } from "src/types/offer/ItemNameCategoryDiscount";
import type { OfferScenario } from "src/types/offer/OfferScenario";
import type { SponsoredAdSlot } from "src/types/sponsored-ads";
import type { SponsoredAdScenario } from "src/types/sponsored-ads/SponsoredAdScenario";
import type { MarketingActionActivationTypeEnum } from "src/utils/activationTypes";
import type { Channel } from "./channel";
import type { Offer, OfferAudience, OfferTypeEnum } from "./offer";
import type { Pos } from "./pos";

export enum OfferStatus {
    Created = "created",
    Scheduled = "scheduled",
    Ongoing = "ongoing",
    Cancelled = "cancelled",
    Ended = "ended",
    WaitingNextSlot = "waiting-next-slot",
    Suspended = "suspended",
    Deleted = "deleted",
    Error = "error",
    InCreation = "in-creation",
}

export const convertOfferStatusToNumberMap: Record<OfferStatus, number> = {
    [OfferStatus.InCreation]: 0,
    [OfferStatus.Created]: 1,
    [OfferStatus.Scheduled]: 2,
    [OfferStatus.Ongoing]: 3,
    [OfferStatus.Cancelled]: 4,
    [OfferStatus.Ended]: 5,
    [OfferStatus.WaitingNextSlot]: 6,
    [OfferStatus.Suspended]: 7,
    [OfferStatus.Deleted]: 8,
    [OfferStatus.Error]: 9,
};

export enum ServiceTime {
    Lunch = 0,
    Dinner = 1,
}

interface TypePerformanceMetric {
    inclvat: number;
    exvat: number;
    countTicket: number;
    profit: number;
    meanBasket: number;
    meanBasketExvat: number;
    budget?: number;
    netBudget?: number;
    efficiencyScore?: number | null;
    efficiencyScoreExvat?: number | null;
}

interface SummaryPerformanceMetric {
    shareDiscountSales: number;
    shareDiscountTicket: number;
    averageCountTicketPast: number;
    averageInclvatPast: number;
    averageExvatPast: number;
    averageMeanBasketExvatPast: number;
    averageOfferCountTicketPast: number;
    averageMeanBasketPast: number;
    averageProfitPast: number;
    diffCountTicket: number;
    diffInclvat: number;
    diffExvat: number;
    diffOfferCountTicket: number;
    diffMeanBasket: number;
    diffMeanBasketExvat: number;
    diffProfit: number;
    slots: string[];
    minPastSlot: string | null;
    maxPastSlot: string | null;
    minPastSlotMoment: Moment | null;
    maxPastSlotMoment: Moment | null;
}

export interface DisplayOfferSlotPerformanceMetric {
    visibility: any;
    total: TypePerformanceMetric & {
        newCustomerCount: number;
        shareNewCustomerCount: number;
        uptime: number;
        countPoss: number;
    };
    discount: TypePerformanceMetric;
    offer: TypePerformanceMetric;
    loyalty: TypePerformanceMetric;
    summary: SummaryPerformanceMetric;
}

export interface OfferSlotPerformance {
    scenarioId: number;
    performance: {
        total: {
            inclvat: number;
            exvat: number;
            countTicket: number;
            profit: number;
            meanBasket: number;
            meanBasketExvat: number;
            newCustomerCount: number;
            shareNewCustomerCount: number;
            uptime: number;
            countPoss: number;
        };
        offer: {
            inclvat: number;
            countTicket: number;
            profit: number;
            meanBasket: number;
            budget: number;
            netBudget: number;
            roi: number;
            duoBudget: number | null;
            adCountTicket: number | null;
            profitDuoApproximate: number | null;
            percentageDiscount: number | null;
            efficiencyScore: number | null;
            efficiencyScoreExvat: number | null;
        };
        past: { countTicket: 0.06859454939925855; profit: 53.4616102554675 };
        summary: SummaryPerformanceMetric;
        visibility: OfferVisibility;
    };
    scenario: Pick<
        OfferScenario,
        "audience" | "discount" | "type" | "items" | "categoryItemList"
    > & {
        id: number;
        ids: number[];
        statuses: OfferStatus[];
        duoScenario: Pick<SponsoredAdScenario, "bid" | "slotBudget">;
        audienceList: OfferAudience[];
    };
    scenarioList: OfferScenario[];
    poss: Pick<Pos, "id" | "name">;
    channel: Channel;
    timeslots: string[];
    days: DayOfTheWeek[];
    countSlot: string;
    slotIds: number[];
    slotWeightList: {
        slotId: number;
        weight: number;
        weightDiscount: number;
    }[];
    uptime: string;
    offer: {
        ids: number[];
        statuses: OfferStatus[];
        durationStartFromMidnight: moment.Duration;
        durationEndFromMidnight: moment.Duration;
        scenarioSpecificActivationPayload?: {
            peak?: number;
            firstSlot?: number;
        };
    };
    timeslotList: string[];
    realtimeslotList: string[];
    dowList: number[];
    shiftedDateList: string[];
    posList: {
        id: number;
        name: string;
    }[];
    slotId?: number;
    idList: number[];

    activationTypes: MarketingActionActivationTypeEnum[];
    groupActivationTypes?: MarketingActionActivationTypeEnum[];
    triggerRefValues: TriggerRefValueType;
    offerTypeList: OfferTypeEnum[];
    itemList: ItemNameCategoryDiscount[];
    discountAndMinBasketList: {
        flatDiscount: number | null;
        percentDiscount: number | null;
        type: OfferTypeEnum;
        minBasket: number | null;
        id: number;
    }[];
    channelList: Channel[];
    categoryList: {
        id: number;
        categoryName: string;
    }[];
    parallelismDegree: number;
}

export interface OfferSlot extends MarketingActionSlot {
    id: number;
    createdDateUTC: Date;
    status: OfferStatus;
    metaData?: MarketingActionSlotMetaData;
    pos: Pos;
    posId: number;
    channel: Channel;
    channelId: number;
    offer: Offer;
    serviceTime: ServiceTime;
    scheduledStartDateUTC: Date;
    scheduledEndDateUTC: Date;
    now: boolean;
    metricsBase?: OfferMetrics;
    metricsControl?: OfferMetrics;
    performance?: DisplayOfferSlotPerformanceMetric;
    visibility?: any;
    //cSpell: disable-next-line
    visib?: any;
    itemDiscounts?: ItemNameCategoryDiscount[];
    pastPerformances?: any;
    computationStartDateUTC: Date;
    computationEndDateUTC: Date;
    computedCarousel: number;
    ancestorSlotId?: number;
    successor: OfferSlot;
    ancestor?: OfferSlot;
    shiftedDate: string;
    remoteId?: string;
    scenario: OfferScenario;
    duoSlot?: SponsoredAdSlot;
    nextFetchMetricsDateUTC?: Date;
    inclvatDiscount?: number;
    budgetDiscount?: number;
    meanBasketDiscount?: number;
    countTicketDiscount?: number;
    inactiveCustomerCount?: number;
    newCustomerCount?: number;
}

export interface OfferMetrics {
    countBasket: number;
    averageBasket: number;
    sales: number;
    profit: number;
}

export interface OfferVisibility {
    carrousel: number;
    ranking: number;
}
